import { Injectable, NgZone, effect, inject, signal } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from '@app/../environments/environment';
import { Observable, Subject, filter, map, share } from 'rxjs';
import { AuthentificationService } from './authentification.service';
import { EntitiesChangedMessage, EntityChangeInfo, EntityChangePayload } from '../_models/SignalR.models';
import { CurrentTenantService } from './CurrentTenantService';


@Injectable({ providedIn: 'root' })
export class SignalRNotificationService {
  private hubConnection!: signalR.HubConnection;
  private messageSubject = new Subject<EntitiesChangedMessage>();
  private subscribedEntityTypes = new Set<string>(); // pour réabonnement automatique
  private currentTenantIdInUse: string | null = null; // pour éviter réinitialisations inutiles

  private authService = inject(AuthentificationService);

  public messages$ = this.messageSubject.asObservable();
  private tenantService = inject(CurrentTenantService);

  constructor() {
    effect(() => {
      const tenantId = this.tenantService?.currentClientId();
      // Normaliser les cas "vide"
      const isEmpty = tenantId === null || tenantId === undefined || tenantId === '';

      if (!isEmpty) {
        // si on est déjà connecté pour le même tenant, ne rien faire
        if (this.hubConnection?.state === signalR.HubConnectionState.Connected && this.currentTenantIdInUse === tenantId) {
          return;
        }
        this.currentTenantIdInUse = tenantId;
        this.initializeConnection();
      } else {
        // si tenantId devient vide, arrêter proprement la connexion ???? ATTENTION : à revoir pour les collection en sommeil
        // AJOUTER un mécanisme de veille pour forcer le raffraichissement aen cas de decalage data après reveil 
        this.currentTenantIdInUse = null;
        this.stopConnection().catch(err => console.error('Erreur lors de l\'arrêt SignalR', err));
      }
    });


    this.initializeConnection();
  }

  private initializeConnection(): void {
    if (!this.tenantService?.currentClientId()) {
      // console.error('TenantId non défini, impossible de se connecter à SignalR');
      return;
    }

    const hubUrl = `${environment.apiBaseUrl}/ws?tenantId=${encodeURIComponent(this.tenantService?.currentClientId())}`;

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => this.authService.getAccessToken()
      })
      .withAutomaticReconnect()
      .build();

    this.hubConnection.onreconnected(() => {
      console.log('SignalR reconnecté, réabonnement...');
      this.resubscribeAll();
    });

    this.hubConnection.on('RecordsUpdated', (message: EntitiesChangedMessage) => {
      //console.log('[DEBUG - 11] FRONTEND RAW RECEIVED:', JSON.stringify(message, null, 2));
      const typedMessage = message as EntitiesChangedMessage;
      this.messageSubject.next(typedMessage);
    });

    this.startConnection();
  }

  private startConnection(): void {
    this.hubConnection.start()
      .then(() => {
        console.log('SignalR connecté avec tenantId', this.tenantService?.currentTenant()?.id);
        this.resubscribeAll();
      })
      .catch(err => console.error('Erreur de connexion SignalR', err));
  }

  /** Abonnement côté serveur à un type d’entité */
  public async subscribeToEntity(entityType: string): Promise<void> {
    if (!entityType) return;
    this.subscribedEntityTypes.add(entityType);
    // Etat reel de la connexion : sans lui, une liste montee apres coup n'est jamais abonnee.
    if (this.hubConnection?.state !== signalR.HubConnectionState.Connected) return;
    try {
      await this.hubConnection.invoke('SubscribeToEntity', entityType);
      console.log(`Abonné à ${entityType}`);
    } catch (err) {
      console.error(`Erreur abonnement ${entityType}`, err);
    }
  }

  /** Désabonnement côté serveur */
  public async unsubscribeFromEntity(entityType: string): Promise<void> {
    if (!entityType) return;
    this.subscribedEntityTypes.delete(entityType);
    if (this.hubConnection?.state !== signalR.HubConnectionState.Connected) return;
    try {
      await this.hubConnection.invoke('UnsubscribeFromEntity', entityType);
      console.log(`Désabonné de ${entityType}`);
    } catch (err) {
      console.error(`Erreur désabonnement ${entityType}`, err);
    }
  }

  private async resubscribeAll(): Promise<void> {
    for (const et of this.subscribedEntityTypes) {
      try {
        console.log(`[DEBUG 12] - On de réabonne a ${et}`);
        await this.hubConnection.invoke('SubscribeToEntity', et);
      } catch (err) {
        console.error(`Échec réabonnement ${et}`, err);
      }
    }
  }

  /** Observable filtré pour un type d’entité */
  public onEntityChanges(entityType: string): Observable<{ initiators: Record<string, string>, changes: EntityChangePayload[] }> {
    return this.messageSubject.pipe(
      filter(msg => !!msg.entities[entityType]),
      map(msg => ({
        initiators: msg.initiators || {},
        changes: msg.entities[entityType]
      })),
      share()
    );
  }



  public async stopConnection(): Promise<void> {
    if (this.hubConnection) {
      this.subscribedEntityTypes.clear();
      await this.hubConnection.stop();
    }
  }
}


